import { Chess, type Square } from "chess.js";

export type Puzzle = { id: string; fen: string; moves: string; rating: number; themes: string[]; opening?: string; game: string };
export type PuzzleProgress = { rating: number; seen: string[]; solved: string[]; streak: number };
export const EMPTY_PROGRESS: PuzzleProgress = { rating: 1200, seen: [], solved: [], streak: 0 };
export const PUZZLE_STORAGE_KEY = "atlas:puzzles";
export const PAGE_SIZE = 12;

const LABELS: Record<string, string> = {
  mateIn1: "Mate in 1", mateIn2: "Mate in 2", mateIn3: "Mate in 3", mateIn4: "Mate in 4", mateIn5: "Mate in 5",
  mate: "Checkmate", oneMove: "One move", veryLong: "Extended line", xRayAttack: "X-ray attack", attackingF2F7: "Attack on f2 / f7", superGM: "Grandmaster game",
};
export function themeLabel(theme: string) {
  return LABELS[theme] ?? theme.replace(/([a-z])([A-Z0-9])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
}
export function openingLabel(opening?: string) { return opening?.replaceAll("_", " ") ?? "From a real game"; }

const PRIORITY = ["mateIn1", "mateIn2", "mateIn3", "mateIn4", "mateIn5", "smotheredMate", "backRankMate", "fork", "pin", "skewer", "sacrifice", "promotion", "discoveredAttack", "deflection", "quietMove", "hangingPiece", "defensiveMove", "endgame"];
export function puzzleTheme(puzzle: Puzzle) { return PRIORITY.find((t) => puzzle.themes.includes(t)) ?? puzzle.themes[0] ?? "tactics"; }
export function puzzleTitle(puzzle: Puzzle) {
  const theme = puzzleTheme(puzzle);
  return theme === "crushing" ? "Winning combination" : theme === "advantage" ? "Find the advantage" : theme === "equality" ? "Hold the position" : themeLabel(theme);
}
export function puzzleMoveCount(puzzle: Puzzle) { return Math.floor(puzzle.moves.trim().split(/\s+/).length / 2); }
export function puzzleSide(puzzle: Puzzle): "white" | "black" { return puzzle.fen.split(" ")[1] === "w" ? "black" : "white"; }
export function puzzleObjective(puzzle: Puzzle) {
  const mate = puzzle.themes.find((t) => /^mateIn[1-5]$/.test(t));
  if (mate) { const n = Number(mate.at(-1)); return `Find checkmate in ${n} ${n === 1 ? "move" : "moves"}.`; }
  if (puzzle.themes.includes("mate")) return "Find the continuation that leads to checkmate.";
  if (puzzle.themes.includes("equality")) return "Find the best defense to hold the position.";
  return "Find the best move, then follow the winning continuation.";
}
/** Lichess stores the position BEFORE the opponent's setup move. */
export function puzzlePosition(puzzle: Puzzle) {
  const game = new Chess(puzzle.fen);
  const u = puzzle.moves.split(" ")[0];
  const move = game.move({ from: u.slice(0, 2), to: u.slice(2, 4), promotion: u[4] });
  return { fen: game.fen(), lastMove: { from: move.from as Square, to: move.to as Square }, san: move.san };
}

export type PuzzleFilters = { query: string; theme: string; level: string; status: string; sort: string };
export function filterPuzzles(all: Puzzle[], filters: PuzzleFilters, progress: PuzzleProgress) {
  const query = filters.query.trim().toLowerCase().replace(/^#/, "");
  const seen = new Set(progress.seen), solved = new Set(progress.solved);
  const filtered = all.filter((p) => {
    if (filters.theme && !p.themes.includes(filters.theme)) return false;
    if (filters.level === "beginner" && p.rating >= 1000) return false;
    if (filters.level === "intermediate" && (p.rating < 1000 || p.rating >= 1600)) return false;
    if (filters.level === "advanced" && (p.rating < 1600 || p.rating >= 2200)) return false;
    if (filters.level === "expert" && p.rating < 2200) return false;
    if (filters.status === "unplayed" && seen.has(p.id)) return false;
    if (filters.status === "solved" && !solved.has(p.id)) return false;
    if (filters.status === "practice" && (!seen.has(p.id) || solved.has(p.id))) return false;
    return !query || `${p.id} ${openingLabel(p.opening)} ${p.themes.map(themeLabel).join(" ")}`.toLowerCase().includes(query);
  });
  return filtered.sort((a, b) => {
    if (filters.sort === "rating-asc") return a.rating - b.rating || a.id.localeCompare(b.id);
    if (filters.sort === "rating-desc") return b.rating - a.rating || a.id.localeCompare(b.id);
    if (filters.sort === "shortest") return puzzleMoveCount(a) - puzzleMoveCount(b) || a.rating - b.rating;
    return Math.abs(a.rating - progress.rating) - Math.abs(b.rating - progress.rating) || a.id.localeCompare(b.id);
  });
}

export function readPuzzleProgress(value: unknown): PuzzleProgress {
  const p = value && typeof value === "object" ? value as Record<string, unknown> : {};
  const ids = (v: unknown) => Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === "string"))] : [];
  return {
    rating: typeof p.rating === "number" && Number.isFinite(p.rating) ? Math.max(100, Math.round(p.rating)) : 1200,
    seen: ids(p.seen), solved: ids(p.solved),
    streak: typeof p.streak === "number" && Number.isFinite(p.streak) ? Math.max(0, Math.floor(p.streak)) : 0,
  };
}

/** An attempt changes the rating once. Replays and assisted solves are practice. */
export function recordPuzzleResult(progress: PuzzleProgress, puzzle: Puzzle, won: boolean, assisted = false): PuzzleProgress {
  const rated = !progress.seen.includes(puzzle.id) && !assisted;
  const expected = 1 / (1 + Math.pow(10, (puzzle.rating - progress.rating) / 400));
  return {
    rating: rated ? Math.max(100, Math.round(progress.rating + 24 * ((won ? 1 : 0) - expected))) : progress.rating,
    seen: [...new Set([...progress.seen, puzzle.id])],
    solved: won ? [...new Set([...progress.solved, puzzle.id])] : progress.solved,
    streak: rated ? (won ? progress.streak + 1 : 0) : progress.streak,
  };
}
